import { useLingui } from '@lingui/react/macro';
import { Box, SxProps, Theme, Tooltip } from '@mui/material';

import CircleCheckmarkIcon from '@watershed/icons/components/CircleCheckmark';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import mixinSx from '@watershed/style/mixinSx';

import Button, { ButtonProps } from './Button';
import { Dialog, DialogProps } from './Dialog';
import Form, { formSx } from './Form/Form';
import ProgressButton from './ProgressButton';

const dialogFormSx: SxProps<Theme> = mixinSx(formSx, {
  paddingBlock: 2.5,
  '& > section': {
    marginTop: -2,
    paddingBottom: 2,
  },
});

interface DialogFormExtraProps {
  disabled?: boolean;
  disabledSubmitTooltip?: React.ReactNode;
  submit?: React.ReactNode;
  cancel?: React.ReactNode;
  submitIcon?: React.ReactNode | null;
  submitIconPosition?: 'start' | 'end';
  submitColor?: ButtonProps['color'];
  isSubmitting?: boolean;
  extraButton?: React.ReactNode;
  showCancelButton?: boolean;
  showSubmitButton?: boolean;
}

export type DialogFormProps = DialogFormExtraProps &
  Pick<
    DialogProps,
    | 'variant'
    | 'header'
    | 'onClose'
    | 'maxWidth'
    | 'children'
    | 'className'
    | 'open'
    | 'disableBackdropClick'
    | 'sx'
    | 'contentSx'
    | 'componentSx'
  >;

function DialogForm({
  submit,
  disabled = false,
  submitIcon = <CircleCheckmarkIcon />,
  submitIconPosition = 'start',
  submitColor = 'primary',
  cancel,
  showCancelButton = true,
  showSubmitButton = true,
  disabledSubmitTooltip = undefined,
  className,
  header,
  isSubmitting,
  extraButton,
  componentSx,
  contentSx,
  ...props
}: DialogFormProps) {
  const { t } = useLingui();

  submit ??= t`Submit`;
  cancel ??= t`Cancel`;

  return (
    <Dialog
      {...props}
      disableEscapeKeyDown
      header={
        header
          ? {
              ...header,
              title: header?.title || '',
              hideClose:
                header['hideClose'] === undefined ? true : header['hideClose'],
            }
          : undefined
      }
      actions={
        <>
          {extraButton && (
            <>
              {extraButton}
              <Box flexGrow={1}></Box>
            </>
          )}
          {showCancelButton &&
            (typeof cancel === 'string' ? (
              <Button
                data-testid={TestIds.CloseDialogButton}
                onClick={props.onClose}
                key="cancel"
              >
                {cancel}
              </Button>
            ) : (
              cancel
            ))}
          {showSubmitButton && (
            <Tooltip
              title={disabled ? (disabledSubmitTooltip ?? '') : ''}
              sx={{
                // DialogActions applies spacing using a ":not(:first-of-type)" selector,
                // so we need to bring our own spacing for the disabled toolip
                marginLeft: 1,
              }}
            >
              {typeof submit === 'string' ? (
                <Box>
                  <ProgressButton
                    type="submit"
                    color={submitColor}
                    startIcon={
                      submitIconPosition === 'start' ? submitIcon : null
                    }
                    endIcon={submitIconPosition === 'end' ? submitIcon : null}
                    disabled={disabled || isSubmitting}
                    isInProgress={!!isSubmitting}
                    key="submit"
                    data-testid={TestIds.DialogFormSubmitButton}
                  >
                    {submit}
                  </ProgressButton>
                </Box>
              ) : (
                <>{submit}</>
              )}
            </Tooltip>
          )}
        </>
      }
      component={Form}
      componentSx={mixinSx(
        {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          gap: 0,
        },
        componentSx
      )}
      contentSx={mixinSx(dialogFormSx, contentSx)}
    />
  );
}

export default DialogForm;
